// PipelineSignalsCard — T2.16f. Listet Großaufträge / HR-Risiken /
// Eskalationen aus pipeline_signal-Tabelle. Klick auf Item öffnet
// Detail-Drawer mit Mail-Excerpt + Empfehlung.

import { useState } from "react";

import { PipelineSignalItem, PipelineSignalsResponse, formatEUR } from "../../lib/api";
import { firmaDot } from "../../lib/firma-colors";

interface Props {
  data?: PipelineSignalsResponse;
  loading?: boolean;
}

const KATEGORIE_BADGE: Record<string, string> = {
  grossauftrag: "bg-emerald-500/20 text-emerald-200 border-emerald-500/40",
  angebot_eingang: "bg-sky-500/20 text-sky-200 border-sky-500/40",
  hr_risiko: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  kuendigung: "bg-rose-500/20 text-rose-200 border-rose-500/40",
  kunden_eskalation: "bg-amber-500/20 text-amber-200 border-amber-500/40",
  sonstiges: "bg-slate-700/50 text-slate-300 border-slate-600/50",
};

const KATEGORIE_LABEL: Record<string, string> = {
  grossauftrag: "Großauftrag",
  angebot_eingang: "Angebot",
  hr_risiko: "HR-Risiko",
  kuendigung: "Kündigung",
  kunden_eskalation: "Eskalation",
  sonstiges: "Sonstiges",
};

export default function PipelineSignalsCard({ data, loading }: Props) {
  const [drawer, setDrawer] = useState<PipelineSignalItem | null>(null);

  if (loading) {
    return <div className="text-xs text-slate-400">Lade Pipeline-Signals …</div>;
  }
  const items = data?.items ?? [];
  if (items.length === 0) {
    return (
      <div className="text-xs text-slate-400">
        Keine Pipeline-Signale offen. Bridge-Daemon laeuft 06:30 + alle 30 min;
        neue Grossauftraege/Auftragsbestaetigungen aus Mail-Briefing landen
        hier.
      </div>
    );
  }
  return (
    <>
      <h3 className="text-[10px] font-medium text-slate-100 uppercase tracking-wide mb-2">
        Pipeline-Signale ({items.length})
      </h3>
      <ul className="space-y-1.5">
        {items.slice(0, 3).map((it) => {
          const dot = firmaDot(it.firma_id);
          const badge = KATEGORIE_BADGE[it.kategorie] ?? KATEGORIE_BADGE.sonstiges;
          return (
            <li key={it.id}>
              <button
                type="button"
                onClick={() => setDrawer(it)}
                className="w-full flex items-baseline gap-2 text-xs text-left min-h-11 px-2 rounded hover:bg-slate-800/40"
              >
                <span className={`h-2 w-2 rounded-full shrink-0 ${dot}`} aria-hidden />
                <span
                  className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${badge}`}
                >
                  {KATEGORIE_LABEL[it.kategorie] ?? it.kategorie}
                </span>
                <span className="flex-1 truncate text-slate-200">{it.titel}</span>
                {it.betrag_eur && (
                  <span className="shrink-0 tabular-nums text-slate-100 font-semibold">
                    {formatEUR(it.betrag_eur)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
        {items.length > 3 && (
          <li className="text-[11px] text-sky-300/80 pl-2">
            +{items.length - 3} weitere — Klick zum Drawer
          </li>
        )}
      </ul>
      {drawer && <SignalDrawer item={drawer} onClose={() => setDrawer(null)} />}
    </>
  );
}

function SignalDrawer({
  item,
  onClose,
}: {
  item: PipelineSignalItem;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Pipeline-Signal Detail"
    >
      <div
        className="w-full sm:max-w-lg bg-slate-900 border-t sm:border border-slate-700 sm:rounded-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-baseline justify-between gap-3 mb-3">
          <h3 className="text-base font-semibold text-slate-100">{item.titel}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="h-11 w-11 inline-flex items-center justify-center rounded-lg text-slate-300 hover:bg-slate-800"
          >
            ✕
          </button>
        </header>
        <dl className="grid grid-cols-2 gap-3 text-xs mb-4">
          <Stat label="Firma" value={item.firma_display} />
          <Stat
            label="Kategorie"
            value={KATEGORIE_LABEL[item.kategorie] ?? item.kategorie}
          />
          {item.betrag_eur && <Stat label="Betrag" value={formatEUR(item.betrag_eur)} />}
          {item.erwartetes_datum && (
            <Stat label="Erwartet" value={item.erwartetes_datum} />
          )}
          <Stat label="Status" value={item.status} />
        </dl>
        {item.beschreibung && (
          <div className="text-xs text-slate-300 leading-relaxed bg-slate-950 border border-slate-800 rounded-lg p-3 max-h-48 overflow-y-auto">
            {item.beschreibung}
          </div>
        )}
        <p className="mt-3 text-[11px] text-slate-500">
          Quelle: <code className="font-mono">pipeline_signal</code> via
          briefing_daemon · Erfasst {item.created_at.slice(0, 10)}
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-widest text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-slate-100 font-medium">{value}</dd>
    </div>
  );
}
